import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService } from "src/app/services/auth.service";

const RESEND_COOLDOWN_SECONDS = 59;

@Component({
  selector: "app-verify-email",
  templateUrl: "./verify-email.component.html",
  styleUrls: ["./verify-email.component.scss"],
})
export class VerifyEmailComponent implements OnInit, OnDestroy {
  email = "";
  resendCooldownSeconds = RESEND_COOLDOWN_SECONDS;
  isSendError = false;

  private _countdownIntervalId?: ReturnType<typeof setInterval>;

  constructor(
    private readonly _authService: AuthService,
    private readonly _router: Router,
    private readonly _route: ActivatedRoute,
  ) {}

  get formattedCooldown(): string {
    const minutes = Math.floor(this.resendCooldownSeconds / 60);
    const seconds = this.resendCooldownSeconds % 60;

    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }

  ngOnInit(): void {
    const token = this._route.snapshot.queryParamMap.get("token");
    const email = this._route.snapshot.queryParamMap.get("email");

    if (token) {
      this.confirmEmail(token, email);
      return;
    }

    this.email = (history.state as { email?: string })?.email ?? "";

    // The confirmation email is already sent by sign-up; "Send again" is the only manual trigger.
    if (!this.email) {
      this.isSendError = true;
      this.resendCooldownSeconds = 0;
      return;
    }

    this.startCountdown();
  }

  ngOnDestroy(): void {
    clearInterval(this._countdownIntervalId);
  }

  resend() {
    if (this.resendCooldownSeconds > 0) {
      return;
    }

    this.sendConfirmationEmail();
  }

  private confirmEmail(token: string, email: string) {
    this._authService.confirmEmail(token, email).subscribe({
      next: () => this.navigateToDashboard(),
      error: () => this.navigateToDashboard(),
    });
  }

  private sendConfirmationEmail() {
    if (!this.email) {
      this.isSendError = true;
      this.resendCooldownSeconds = 0;
      return;
    }

    this.isSendError = false;

    this._authService.sendEmailConfirmation(this.email).subscribe({
      next: () => this.startCountdown(),
      error: () => {
        this.isSendError = true;
        this.resendCooldownSeconds = 0;
      },
    });
  }

  private startCountdown() {
    clearInterval(this._countdownIntervalId);
    this.resendCooldownSeconds = RESEND_COOLDOWN_SECONDS;

    this._countdownIntervalId = setInterval(() => {
      this.resendCooldownSeconds -= 1;

      if (this.resendCooldownSeconds <= 0) {
        clearInterval(this._countdownIntervalId);
      }
    }, 1000);
  }

  private navigateToDashboard() {
    this._router.navigate([""]);
  }
}
